/**
 * Proposal builder line-item contract (fix/proposal-line-validation).
 *
 * The builder only lets the operator pick a catalogue product or type a
 * description — there is no code input. The old submit check demanded
 * `product_code_snapshot` too, so every "Custom..." line failed with
 * "Each line needs a code and description" no matter what was typed.
 *
 * Contract:
 * - description is required (non-empty after trim);
 * - product code is system metadata, never typed by the operator:
 *   catalogue line -> the catalogue code snapshot (if the product has one),
 *   custom line    -> CUSTOM_PRODUCT_CODE;
 * - converting a catalogue line back to Custom drops the catalogue code;
 * - a line whose category is not one of the known sections is shown in an
 *   "Other items" section rather than hidden-but-still-validated.
 *
 * Mirrors QuoteLineItemIn in backend/routes/quotes.py.
 */

export const CUSTOM_PRODUCT_CODE = "CUSTOM";
export const OTHER_SECTION_KEY = "__other__";
export const DEFAULT_SECTION_KEY = "user_licenses";

/** Section a line renders in. Blank category keeps the historical user_licenses default. */
export function sectionKeyForLine(line, knownKeys) {
  const cat = (line && line.category_snapshot) || DEFAULT_SECTION_KEY;
  return knownKeys.includes(cat) ? cat : OTHER_SECTION_KEY;
}

/**
 * Code to persist for a line. A code already stored on the line is kept
 * (catalogue snapshot, or a legacy/system code such as RENEWAL on an
 * edited proposal); a custom line with no code gets CUSTOM.
 */
export function lineProductCode(line) {
  const code = String((line && line.product_code_snapshot) || "").trim();
  if (code) return code;
  return line && line.product_id ? "" : CUSTOM_PRODUCT_CODE;
}

/** A line switched back to "Custom..." — no product, no stale catalogue code. */
export function toCustomLine(line) {
  return { ...line, product_id: "", product_code_snapshot: "" };
}

/**
 * "Services, line 2" for each flat `lines` index — the section and the
 * row number as the operator sees it (1-based within the section).
 */
export function lineLocations(lines, sections) {
  const knownKeys = sections.map(s => s.key);
  const labelFor = (key) => key === OTHER_SECTION_KEY
    ? "Other items"
    : (sections.find(s => s.key === key) || {}).label || key;
  const rowCounter = {};
  return (lines || []).map(l => {
    const key = sectionKeyForLine(l, knownKeys);
    rowCounter[key] = (rowCounter[key] || 0) + 1;
    return `${labelFor(key)}, line ${rowCounter[key]}`;
  });
}

/**
 * Validate every line. `sections` is the builder's [{key, label}] list.
 * Returns { ok, message, invalidFields } where message names the section
 * and row, and invalidFields maps a flat line index to the input(s) to
 * highlight ("description" | "quantity" | "unit_price").
 */
export function validateProposalLines(lines, sections) {
  const where = lineLocations(lines, sections);
  const problems = [];
  (lines || []).forEach((l, index) => {
    const qty = Number(l.quantity);
    const price = Number(l.unit_price);
    let msg = null;
    let fields = [];
    if (!String(l.description_snapshot || "").trim()) { msg = "enter a description."; fields = ["description"]; }
    else if (!Number.isFinite(qty) || !Number.isFinite(price)) {
      msg = "quantity and unit price must be valid numbers.";
      fields = [!Number.isFinite(qty) && "quantity", !Number.isFinite(price) && "unit_price"].filter(Boolean);
    }
    else if (qty === 0) { msg = "quantity cannot be zero."; fields = ["quantity"]; }
    // Credits are a negative quantity OR a negative price; both negative
    // multiplies back to a positive charge.
    else if (qty < 0 && price < 0) { msg = "use a negative quantity or a negative unit price for a credit, but not both."; fields = ["quantity", "unit_price"]; }
    if (msg) problems.push({ index, fields, text: `${where[index]}: ${msg}` });
  });
  if (problems.length === 0) return { ok: true, message: "", invalidFields: {} };
  const extra = problems.length > 1 ? ` (${problems.length - 1} more line${problems.length > 2 ? "s" : ""} also need attention.)` : "";
  return {
    ok: false,
    message: `${problems[0].text}${extra}`,
    invalidFields: Object.fromEntries(problems.map(p => [p.index, p.fields])),
  };
}

/** Build the line_items payload sent to POST/PUT /api/quotes. */
export function buildLineItemsPayload(lines, currencyCode) {
  return (lines || []).map(l => ({
    product_id: l.product_id || null,
    product_code_snapshot: lineProductCode(l),
    description_snapshot: String(l.description_snapshot || "").trim(),
    quantity: Number(l.quantity),
    unit_price: Number(l.unit_price),
    // One proposal, one currency: never a stale per-line code.
    line_currency_code: currencyCode || l.line_currency_code,
    line_discount_amount: Number(l.line_discount_amount || 0),
    category_snapshot: l.category_snapshot || DEFAULT_SECTION_KEY,
    pricing_type_snapshot: l.pricing_type_snapshot || "annual",
    monthly_addon_unit_price: l.category_snapshot === "services" ? Number(l.monthly_addon_unit_price || 0) : 0,
  }));
}

const FIELD_LABELS = {
  description_snapshot: "description", quantity: "quantity", unit_price: "unit price",
  line_discount_amount: "discount", monthly_addon_unit_price: "monthly add-on",
};

/**
 * FastAPI 422 bodies carry `detail` as a list of {loc, msg}. Turn them into
 * "Services, line 2: …" (or "<field>: …" outside the lines) instead of a
 * generic "Failed to save". `locations` is lineLocations() for the lines
 * that were sent, so the row matches what the operator sees. Returns null
 * when there is nothing usable so the caller keeps its own fallback.
 */
export function formatValidationDetail(detail, locations = []) {
  if (!Array.isArray(detail) || detail.length === 0) return null;
  return detail.map(d => {
    const loc = Array.isArray(d && d.loc) ? d.loc : [];
    const msg = String((d && d.msg) || "Invalid value").replace(/^Value error, /, "");
    const i = loc.indexOf("line_items");
    const idx = i !== -1 ? loc[i + 1] : undefined;
    if (Number.isInteger(idx)) {
      const field = loc[i + 2];
      const label = FIELD_LABELS[field] || field;
      // Model-level checks (no field) and the description check already
      // read as a sentence; name the field otherwise ("Field required").
      const text = !field || field === "description_snapshot" ? msg : `${label}: ${msg}`;
      return `${locations[idx] || `Line ${idx + 1}`}: ${text}`;
    }
    const field = loc.filter(x => x !== "body").join(".");
    return field ? `${field}: ${msg}` : msg;
  }).join(" ");
}
